import {
  useLinkBuilder,
  getActionFromState,
  getStateFromPath,
  useNavigation,
} from '@react-navigation/native';
import * as Linking from 'expo-linking';

import { getPathFromParams, getFocusedRouteFromState } from '@utils';
import { useLinking } from './useLinking';
import config from '@config';
import { screens } from '@navigation';

type BuildLinkPropsType<
  TParamList extends ReactNavigation.RootParamList = ReactNavigation.RootParamList,
  TRouteName extends keyof TParamList = keyof TParamList,
> = {
  screen: Extract<TRouteName, string>;
  params?: TParamList[TRouteName];
};

export function useLink() {
  const linking = useLinking();
  const buildLinkForRoute = useLinkBuilder();
  const nav = useNavigation();

  /**
   * Custom link builder function that build link based on LinkingConfig
   * @param {BuildLinkPropsType}
   * @returns {string}
   */
  function buildLink({ screen, params }: BuildLinkPropsType) {
    return getPathFromParams(screen, params, linking.options?.config);
  }

  /**
   * Custom link builder function that build link based on LinkingConfig and add app url
   * @param {BuildLinkPropsType}
   * @returns {string}
   */
  function buildFullLink(props: BuildLinkPropsType) {
    return `${config.APP_URL}${buildLink(props)}`;
  }

  function buildFullLinkForRoute(name: string, params?: object) {
    return `${config.APP_URL}${buildLinkForRoute(name, params)}`;
  }

  function linkTo(url: string) {
    const { path } = Linking.parse(url);
    const state = getStateFromPath(
      `/${path}`,
      linking.options?.config,
    );

    if (state) {
      const action = getActionFromState(
        state,
        linking.options?.config,
      );

      if (action) {
        const focusedRoute = getFocusedRouteFromState(state);

        // TODO: find a better way to handle exceptions like Tutorial screen
        if (focusedRoute && focusedRoute.name === screens.Tutorial) {
          nav.dispatch({ ...action, type: 'REPLACE' });
        } else {
          nav.dispatch(action);
        }
      } else {
        console.warn('linkTo: action is undefined');
      }
    } else {
      console.warn('linkTo: state is undefined');
    }
  }

  return { buildLink, buildFullLink, buildFullLinkForRoute, linkTo };
}
